import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { v4 as uuid } from 'uuid'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../../components/Icons'

import ChecklistTemplateNavbar from './Components/ChecklistTemplateNavbar'
import ChecklistTemplateDetailsBody from './Components/ChecklistTemplateDetailsBody'

const ChecklistTemplateForm = ({
  templateMode,
  data,
  buildingId,
  handleClose,
  submitChecklist,
}) => {
  const { t } = useTranslation(['checklists-legacy'])
  const [editModeList, setEditModeList] = useState(data.rooms.length ? [] : ['name'])
  const [isSubmitting, setSubmitting] = useState(false)
  const [editData, setEditData] = useState(data || null)
  const [selectedRoom, selectRoom] = useState(data.rooms.length ? data.rooms[0] : null)
  const [typeFilter, setTypeFilter] = useState(data.type || null)
  const [dateTrigger, setDateTrigger] = useState(null)
  const [continueTrigger, setContinueTrigger] = useState(data.dueDateCondition || null)
  const [withinDays, setWithinDays] = useState(data.dueDateDays || null)
  const [errors, setErrors] = useState([])
  const [errorMessage, setErrorMessage] = useState(null)

  // TO FIX:

  const isDisabled = false || templateMode === 'preview'
  const isGuided = editData.isGuided

  const selectedRoomItems = editData.rooms.find((room) => room.id === selectedRoom.id)
    ? editData.rooms.find((room) => room.id === selectedRoom.id).roomItems
    : []

  const handleEditValue = (inputName, inputValue) => {
    setEditData({ ...editData, [inputName]: inputValue })
  }

  const addNewRoom = () => {
    const genericId = uuid()
    const newRooms = [
      ...editData.rooms,
      {
        id: genericId,
        name: t('checklists-legacy:templateForm.defaultLocationName', {
          number: editData.rooms.length + 1,
        }),
        roomItems: [],
      },
    ]
    setEditData({
      ...editData,
      rooms: newRooms,
    })
    selectRoom(newRooms[newRooms.length - 1])
  }

  const removeExistingRoom = (e, roomId) => {
    e.stopPropagation()
    const filteredRooms = editData.rooms.filter((room) => room.id !== roomId)

    if (selectedRoom.id === roomId) selectRoom(filteredRooms.length ? filteredRooms[0] : null)

    setEditData({
      ...editData,
      rooms: filteredRooms,
    })
  }

  const changeRoomItemParam = (roomItemId, paramToChange, value) => {
    const { rooms } = editData
    const newData = rooms.map((room) =>
      room.id === selectedRoom.id
        ? {
            ...room,
            roomItems: room.roomItems.map((item) =>
              item.id === roomItemId ? { ...item, [paramToChange]: value } : item,
            ),
          }
        : room,
    )
    setEditData((prevEditData) => ({ ...prevEditData, rooms: newData }))
  }

  const validateField = (value, fieldName, minSize, maxSize) => {
    if ((!value || value < minSize || value > maxSize) && !errors.includes(fieldName)) {
      setErrors([...errors, fieldName])
    } else if (value >= minSize && value <= maxSize && errors.includes(fieldName)) {
      setErrors(errors.filter((error) => error !== fieldName))
    }
  }

  const validateAll = () => {
    const newErrorsToAdd = []
    const oldErrorsToRemove = []
    const filteredErrors = errors.filter((err) => !err.startsWith('roomId'))

    if (!editData.name.length && !errors.includes('name')) {
      newErrorsToAdd.push('name')
    } else if (editData.name.length && errors.includes('name')) {
      oldErrorsToRemove.push('name')
    }

    editData.rooms.forEach((room) => {
      if (!room.name) {
        newErrorsToAdd.push(`roomId-${room.id}-locationName`)
      }

      if (!room.roomItems.length) {
        newErrorsToAdd.push(`roomId-${room.id}`)
      } else {
        room.roomItems.forEach((item) => {
          if (!item.name) {
            newErrorsToAdd.push(`roomId-${room.id}-objectId-${item.id}`)
          }
          if (isGuided) {
            if (item.requirePhoto && !item.description) {
              newErrorsToAdd.push(`roomId-${room.id}-objectId-${item.id}-description`)
            }
            if (!item.requireRating && !item.requirePhoto) {
              newErrorsToAdd.push(`roomId-${room.id}-objectId-${item.id}-toggles`)
            }
          }
        })
      }

      if (newErrorsToAdd.some((err) => err.startsWith(`roomId-${room.id}-objectId`))) {
        newErrorsToAdd.push(`roomId-${room.id}`)
      }
    })
    setErrors([
      ...filteredErrors.filter((error) => !oldErrorsToRemove.includes(error)),
      ...newErrorsToAdd,
    ])
  }

  useEffect(() => {
    validateAll()
  }, [editData])

  const validateGeneralInformations = () => {
    const newErrors = []

    if (!typeFilter) newErrors.push('typeFilter')
    if (!continueTrigger) newErrors.push('continueTrigger')
    if (!withinDays) newErrors.push('withinDays')

    editData.rooms.forEach((room) => {
      room.roomItems.forEach((object) => {
        if (!object.name) newErrors.push(`roomId-${room.id}-objectId-${object.id}`)
      })
    })

    const otherErrors = errors.filter(
      (err) =>
        !['typeFilter', 'continueTrigger', 'withinDays'].includes(err) && !err.includes('objectId'),
    )

    const combinedErrors = [...otherErrors, ...newErrors]

    setErrors(combinedErrors)

    return combinedErrors.length === 0
  }

  const handleSubmit = () => {
    setSubmitting(true)
    if (validateGeneralInformations()) {
      // cleaning questions fields which are not filled
      const filteredEmptyQuestionsInRoomsForGuided = editData.rooms.map((room, index) => ({
        ...room,
        listPosition: index + 1,
        roomItems: room.roomItems.map((item) => {
          if (item && item.questions) {
            const filteredQuestions = item.questions.filter((question) => question.question !== '')
            return { ...item, questions: filteredQuestions.length > 0 ? filteredQuestions : null }
          }
          return item
        }),
      }))

      // cleaning filled fields which are not needed during not-guided type
      const filteredRoomsForNotGuided = editData.rooms.map((room, index) => ({
        ...room,
        listPosition: index + 1,
        roomItems: room.roomItems.map((item) => {
          return {
            ...item,
            description: '',
            questions: null,
            requirePhoto: false,
            requireRating: false,
            requireNote: false,
          }
        }),
      }))

      const dueDateTrigger = editData.dueDateTrigger || 'move-in'

      const dataToSend = isGuided
        ? {
            ...editData,
            dueDateTrigger,
            dueDateDays: withinDays,
            dueDateCondition: continueTrigger,
            type: typeFilter,
            isTemplate: data.isTemplate,
            buildingId,
            rooms: filteredEmptyQuestionsInRoomsForGuided,
          }
        : {
            ...editData,
            dueDateTrigger,
            dueDateDays: withinDays,
            dueDateCondition: continueTrigger,
            type: typeFilter,
            isTemplate: data.isTemplate,
            buildingId,
            rooms: filteredRoomsForNotGuided,
          }

      try {
        submitChecklist(
          dataToSend,
          dataToSend.id,
          data.isTemplate ? 'templates' : 'custom',
          handleClose,
        )
      } catch ({ message }) {
        throw new Error(message)
      } finally {
        setSubmitting(false)
      }
    } else {
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (errors.length) {
      setErrorMessage(t('checklists-legacy:templateForm.requiredFieldsMessage'))
    } else {
      setErrorMessage(null)
    }
  }, [errors])

  useEffect(() => {
    if (typeFilter === 'move-out') {
      setContinueTrigger('before')
      setDateTrigger('moveOutDate')
      setErrors(errors.filter((err) => !['typeFilter', 'continueTrigger'].includes(err)))
    } else {
      setDateTrigger('moveInDate')
      setErrors(errors.filter((err) => err !== 'typeFilter'))
    }
  }, [typeFilter])

  useEffect(() => {
    setEditModeList([])
  }, [selectedRoom])

  return (
    <div className={`ctp-checklist-template-form-wrapper ${isSubmitting ? 'is-loading' : ''}`}>
      <ChecklistTemplateNavbar
        typeFilter={typeFilter}
        continueTrigger={continueTrigger}
        dateTrigger={dateTrigger}
        withinDays={withinDays}
        editModeList={editModeList}
        editData={editData}
        errors={errors}
        handleEditValue={handleEditValue}
        setWithinDays={setWithinDays}
        setContinueTrigger={setContinueTrigger}
        validateField={validateField}
        setEditModeList={setEditModeList}
        setTypeFilter={setTypeFilter}
        setErrors={setErrors}
        isSubmitting={isSubmitting}
        isDisabled={isDisabled}
      />
      <div className="template-body-container">
        <div className="side-navigation">
          {editData.rooms.map((room) => (
            <div
              key={room.id}
              className={`nav-element ${errors.includes(`roomId-${room.id}`) ? 'is-error' : ''} ${
                selectedRoom.id === room.id ? 'selected' : ''
              }`}
              onClick={() => selectRoom(room)}
            >
              {room.name ? (
                <div className="element-title">{room.name}</div>
              ) : (
                <div className="element-title placeholder">
                  {t('checklists-legacy:templateForm.locationNamePlaceholder')}
                </div>
              )}
              {editData.rooms.length > 1 ? (
                <span
                  className="remove-room-icon"
                  onClick={(e) => {
                    removeExistingRoom(e, room.id)
                  }}
                >
                  <Icon icon="cross" viewBox={'-3 0 20 20'} />
                </span>
              ) : null}
            </div>
          ))}
          {!isDisabled && (
            <div className="nav-element" onClick={addNewRoom}>
              <Icon icon="cross" viewBox="0 0 23 20" />
              {t('checklists-legacy:templateForm.addLocation')}
            </div>
          )}
        </div>
        {selectedRoom ? (
          <ChecklistTemplateDetailsBody
            templateMode={templateMode}
            errors={errors}
            selectedRoomItems={selectedRoomItems}
            editModeList={editModeList}
            selectedRoom={selectedRoom}
            editData={editData}
            errorMessage={errorMessage}
            changeRoomItemParam={changeRoomItemParam}
            setEditModeList={setEditModeList}
            setEditData={setEditData}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isDisabled={isDisabled}
          />
        ) : null}
      </div>
    </div>
  )
}

ChecklistTemplateForm.propTypes = {
  templateMode: PropTypes.string,
  data: PropTypes.object,
  buildingId: PropTypes.number,
  handleClose: PropTypes.func,
  submitChecklist: PropTypes.func,
}

export default ChecklistTemplateForm
